/**
 * CompanyLeadsDashboard
 *
 * Leads section dashboard for company admin / company owner.
 * Shows KPIs, employee leaderboard, weekly trend, rejection analysis,
 * recent activity feed, and full leads list.
 *
 * Route: /app/company/leads-dashboard
 * Who sees it: company_admin, company_owner
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Skeleton } from 'primereact/skeleton';
import { Chart } from 'primereact/chart';
import { AutoComplete } from 'primereact/autocomplete';
import { TabView, TabPanel } from 'primereact/tabview';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';

import {
    getKpiSummary,
    getTodaysActivity,
    getLeaderboard,
    getAgentLeaderboard,
    getWeeklyTrend,
    getRejectionReasons,
    getRecentActivity,
    getAllLeads,
} from '../../services/companyLeadsDashboardService';
import { fetchPageData } from '../../services/pageDataService';
import { adminLookupService } from '../../services/adminLookupService';
import { getProcessingStatusConfig, PROCESSING_STATUS } from '../../services/leadProcessingService';
import AgentLeadsDrawer from './AgentLeadsDrawer';

// ─── constants ───────────────────────────────────────────────────────────────

// Four time-window options for the leaderboard period dropdown.
// Backend value semantics defined in CompanyLeadsDashboardService::resolvePeriod().
//   today      → start of today  → now
//   this_month → start of month  → now (default)
//   this_year  → start of year   → now
//   all_time   → no date filter (cumulative)
const PERIOD_OPTIONS = [
    { label: 'Daily',   value: 'today' },
    { label: 'Monthly', value: 'this_month' },
    { label: 'Yearly',  value: 'this_year' },
    { label: 'Overall', value: 'all_time' },
];

const SORT_OPTIONS = [
    // Activity (what the employee CREATED)
    { label: 'Most Leads Created', value: 'leads_created' },
    { label: 'Most Active Today', value: 'created_today' },
    { label: 'Largest Pipeline (₹)', value: 'pipeline_value' },
    { label: 'Most Stale Leads', value: 'stale_leads' },
    // Processing (what the employee handled AS PROCESSOR)
    { label: 'Most Assigned', value: 'assigned' },
    { label: 'Most Submitted', value: 'submitted' },
    { label: 'Most Approved', value: 'approved' },
    { label: 'Best Conversion %', value: 'conversion_rate' },
    { label: 'Fastest Processing', value: 'avg_days' },
    { label: 'Name (A–Z)', value: 'name' },
];

const AGENT_SORT_OPTIONS = [
    { label: 'Most Leads', value: 'total_leads' },
    { label: 'Most Disbursed', value: 'disbursed' },
    { label: 'Best Quality %', value: 'quality_pct' },
    { label: 'Best Conversion %', value: 'conversion_pct' },
    { label: 'Most Approved', value: 'approved' },
    { label: 'Name (A–Z)', value: 'name' },
];

// 15 unified lead statuses — single source of truth is PROCESSING_STATUS in leadProcessingService.
// Keep this order in sync with the Lead View page's status progression.
const UNIFIED_STAGE_KEYS = [
    'new_lead', 'offers_generated', 'broadcasted',
    'processing', 'bank_submit', 'bank_queries', 'bank_processing',
    'sanction', 'sanction_queries', 'disbursal_queries', 'disbursal',
    'bank_rejected', 'customer_rejected', 'processor_rejected', 'closed',
];

const STAGE_OPTIONS = [
    { label: 'All Stages', value: null },
    ...UNIFIED_STAGE_KEYS.map((key) => ({
        label: PROCESSING_STATUS[key]?.label || key,
        value: key,
    })),
];

const ACTIVITY_COLORS = {
    new_lead: 'var(--blue-300)',
    documents_uploaded: 'var(--blue-400)',
    offers_generated: 'var(--teal-500)',
    broadcasted: 'var(--purple-500)',
    processor_selected: 'var(--indigo-500)',
    in_processing: 'var(--yellow-600)',
    submitted_to_bank: 'var(--blue-500)',
    approved: 'var(--green-600)',
    rejected: 'var(--red-600)',
    disbursed: 'var(--green-500)',
    closed: 'var(--surface-500)',
    exhausted: 'var(--red-500)',
};

const BREAKDOWN_CONFIG = {
    new_lead: { label: 'New', shade: '400' },
    offers_generated: { label: 'Offers', shade: '600' },
    broadcasted: { label: 'Broadcast', shade: '400' },
    processing: { label: 'Processing', shade: '600' },
    bank_submit: { label: 'Submit', shade: '400' },
    bank_queries: { label: 'Queries', shade: '500' },
    bank_processing: { label: 'Processing', shade: '600' },
    sanction: { label: 'Sanction', shade: '500' },
    sanction_queries: { label: 'Queries', shade: '600' },
    disbursal: { label: 'Disbursal', shade: '500' },
    disbursal_queries: { label: 'Queries', shade: '600' },
    bank_rejected: { label: 'Bank', shade: '400' },
    customer_rejected: { label: 'Customer', shade: '500' },
    processor_rejected: { label: 'Processor', shade: '600' },
};

// ─── helpers ──────────────────────────────────────────────────────────────────

const formatCurrency = (val) => {
    if (!val && val !== 0) return '—';
    const num = Number(val);
    if (num >= 100000) return '₹' + (num / 100000).toFixed(2) + ' L';
    if (num >= 1000) return '₹' + (num / 1000).toFixed(1) + ' K';
    return '₹' + num.toLocaleString('en-IN');
};

const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────

// KPI card with status-colored accent + segmented breakdown bar + trend arrow.
// Color param uses PrimeFlex theme variables (--{color}-500 / --{color}-100 etc).
const KpiCard = ({ title, icon, color, data, loading }) => {
    const changePct = data?.change_pct;
    const isPositive = changePct > 0;
    const isNegative = changePct < 0;
    const breakdown = data?.breakdown || {};
    const breakdownEntries = Object.entries(breakdown).filter(([, v]) => v > 0);
    const breakdownTotal = Object.values(breakdown).reduce((s, v) => s + v, 0) || 1;

    return (
        <div className="col-6 lg:col">
            <Card
                className="h-full shadow-1"
                style={{ borderLeft: `3px solid var(--${color}-500)` }}
                pt={{ body: { className: 'p-2' }, content: { className: 'p-0' } }}
            >
                {loading ? (
                    <>
                        <Skeleton width="60%" height="0.85rem" className="mb-1" />
                        <Skeleton width="40%" height="1.4rem" className="mb-1" />
                        <Skeleton width="80%" height="0.7rem" />
                    </>
                ) : (
                    <>
                        <div className="flex align-items-center justify-content-between mb-1">
                            <span className="text-600 text-xs font-medium">{title}</span>
                            <div
                                className="flex align-items-center justify-content-center border-round"
                                style={{ width: '1.75rem', height: '1.75rem', backgroundColor: `var(--${color}-100)` }}
                            >
                                <i className={`pi ${icon} text-xs`} style={{ color: `var(--${color}-600)` }} aria-hidden="true" />
                            </div>
                        </div>

                        <div className="flex align-items-baseline gap-2 mb-1">
                            <span className="text-900 text-lg font-bold">{data?.this_month ?? 0}</span>
                            <span className="text-500 text-xs">this month</span>
                        </div>

                        {/* Segmented bar */}
                        {breakdownEntries.length > 0 ? (
                            <>
                                <div className="flex border-round overflow-hidden mb-1" style={{ height: '5px' }}>
                                    {breakdownEntries.map(([status, count]) => {
                                        const cfg = BREAKDOWN_CONFIG[status] || { shade: '500' };
                                        return (
                                            <div
                                                key={status}
                                                style={{
                                                    width: `${(count / breakdownTotal * 100).toFixed(1)}%`,
                                                    backgroundColor: `var(--${color}-${cfg.shade})`,
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                                <div className="flex flex-wrap gap-x-2 text-xs mb-1">
                                    {breakdownEntries.map(([status, count]) => {
                                        const cfg = BREAKDOWN_CONFIG[status] || { label: status, shade: '500' };
                                        return (
                                            <span
                                                key={status}
                                                className="flex align-items-center gap-1 white-space-nowrap"
                                                style={{ color: `var(--${color}-700)` }}
                                            >
                                                <span
                                                    className="border-circle flex-shrink-0"
                                                    style={{
                                                        width: '5px',
                                                        height: '5px',
                                                        backgroundColor: `var(--${color}-${cfg.shade})`,
                                                    }}
                                                />
                                                {cfg.label} <strong>{count}</strong>
                                            </span>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <div className="border-round mb-1" style={{ height: '5px', backgroundColor: 'var(--surface-200)' }} />
                        )}

                        <div className="flex align-items-center justify-content-between text-xs mt-2">
                            <div className="flex gap-2">
                                <span className="text-500">Week <strong className="text-700">{data?.this_week ?? 0}</strong></span>
                                <span className="text-500">Prev <strong className="text-700">{data?.last_month ?? 0}</strong></span>
                            </div>
                            {changePct != null && (
                                <span className={`font-semibold ${isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-500'}`}>
                                    <i className={`pi ${isPositive ? 'pi-arrow-up' : isNegative ? 'pi-arrow-down' : 'pi-minus'} text-xs`} aria-hidden="true" />
                                    {' '}{Math.abs(changePct)}%
                                </span>
                            )}
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
};

// ─── Today's Activity tile ────────────────────────────────────────────────────
//
// Denser than KpiCard — a single big number + small label. Designed for the
// morning-glance row so a manager sees "what happened today" at a glance.
// Color-coded by intent:
//   blue   = inflow (leads created)
//   cyan   = throughput (submissions to banks)
//   green  = positive outcome (approvals, disbursals)
//   orange = attention needed (stale leads)
//   purple = future-looking (pipeline value)
//
// IMPORTANT: do NOT pass `tooltip` into <Card title=...> — Card renders the
// `title` prop as a big card heading, which previously turned the tooltip
// into a giant block of text on the tile. Use the native HTML `title=""`
// attribute on the wrapper div so it's a real hover tooltip.
const TodayTile = ({ title, icon, color, value, loading, isCurrency = false, tooltip }) => {
    const formatted = isCurrency
        ? (value >= 10000000
            ? `₹${(value / 10000000).toFixed(1)}Cr`
            : value >= 100000
                ? `₹${(value / 100000).toFixed(1)}L`
                : `₹${Number(value || 0).toLocaleString('en-IN')}`)
        : Number(value || 0).toLocaleString('en-IN');

    return (
        <div className="col-6 md:col-4 lg:col-2" title={tooltip}>
            <Card
                className="h-full shadow-1"
                pt={{ body: { className: 'p-3' }, content: { className: 'p-0' } }}
            >
                {loading ? (
                    <>
                        <Skeleton width="60%" height="0.7rem" className="mb-2" />
                        <Skeleton width="50%" height="1.6rem" />
                    </>
                ) : (
                    <>
                        <div className="flex align-items-center justify-content-between mb-2">
                            <span className="text-600 text-xs font-medium">{title}</span>
                            <div
                                className="flex align-items-center justify-content-center border-round"
                                style={{
                                    width: '1.5rem',
                                    height: '1.5rem',
                                    backgroundColor: `var(--${color}-100)`,
                                }}
                            >
                                <i
                                    className={`pi ${icon}`}
                                    style={{ color: `var(--${color}-600)`, fontSize: '0.75rem' }}
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <div className="text-xl font-bold" style={{ color: `var(--${color}-700)`, lineHeight: 1.1 }}>
                            {formatted}
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CompanyLeadsDashboard = () => {
    const toast = useRef(null);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Initial state restored from URL query params so navigating back from a
    // lead view brings the user to the exact filter state + page they left.
    const parseDateParam = (v) => {
        if (!v) return null;
        const d = new Date(v);
        return Number.isNaN(d.getTime()) ? null : d;
    };
    const initialLeadsPage = Math.max(parseInt(searchParams.get('page') || '1', 10) || 1, 1);

    // KPI
    const [kpi, setKpi] = useState(null);
    const [kpiLoading, setKpiLoading] = useState(true);

    // Today's Activity (morning glance tiles)
    const [today, setToday] = useState(null);
    const [todayLoading, setTodayLoading] = useState(true);

    // Employee Leaderboard
    const [lbData, setLbData] = useState([]);
    const [lbPagination, setLbPagination] = useState({});
    const [lbLoading, setLbLoading] = useState(true);
    const [lbPeriod, setLbPeriod] = useState('this_month');
    const [lbSort, setLbSort] = useState('leads_created');
    const [lbSearch, setLbSearch] = useState('');
    const [lbPage, setLbPage] = useState(1);

    // Agent Leaderboard
    const [agData, setAgData] = useState([]);
    const [agPagination, setAgPagination] = useState({});
    const [agLoading, setAgLoading] = useState(true);
    const [agPeriod, setAgPeriod] = useState('this_month');
    const [agSort, setAgSort] = useState('total_leads');
    const [agSearch, setAgSearch] = useState('');
    const [agPage, setAgPage] = useState(1);
    const [perfTab, setPerfTab] = useState(0);

    // Agent drill-down — opens the leads drawer for one agent at a time.
    // Holds the entire leaderboard row so the drawer header can render the
    // pre-fetched count chips without an extra round-trip.
    const [selectedAgent, setSelectedAgent] = useState(null);

    // Mobile Accordion expanded index for the Agent Performance leaderboard
    // (P17). Reset whenever the result set changes so a stale index doesn't
    // open an unrelated agent on a new page / filter / search.
    const [agMobileIndex, setAgMobileIndex] = useState(null);
    useEffect(() => {
        setAgMobileIndex(null);
    }, [agPage, agPeriod, agSort, agSearch]);

    // Weekly Trend
    const [trend, setTrend] = useState(null);
    const [trendLoading, setTrendLoading] = useState(true);

    // Rejection Reasons
    const [rejections, setRejections] = useState([]);
    const [rejLoading, setRejLoading] = useState(true);

    // Recent Activity
    const [activity, setActivity] = useState([]);
    const [actLoading, setActLoading] = useState(true);

    // All Leads — filter + pagination state restored from URL params
    const [leads, setLeads] = useState([]);
    const [leadsPag, setLeadsPag] = useState({});
    const [leadsLoading, setLeadsLoading] = useState(true);
    const [leadsSearch, setLeadsSearch] = useState(searchParams.get('search') || '');
    const [leadsStage, setLeadsStage] = useState(searchParams.get('stage') || null);
    const [leadsEmployee, setLeadsEmployee] = useState(searchParams.get('employee') || '');
    const [leadsDateFrom, setLeadsDateFrom] = useState(parseDateParam(searchParams.get('from')));
    const [leadsDateTo, setLeadsDateTo] = useState(parseDateParam(searchParams.get('to')));
    const [leadsFirst, setLeadsFirst] = useState((initialLeadsPage - 1) * 15);

    // Employee AutoComplete (leaderboard)
    const [lbEmpQuery, setLbEmpQuery] = useState('');
    const [lbEmpSuggestions, setLbEmpSuggestions] = useState([]);
    const [lbEmpLoading, setLbEmpLoading] = useState(false);
    const lbEmpDebounceRef = useRef(null);

    // Employee AutoComplete (leads list)
    const [empQuery, setEmpQuery] = useState('');
    const [empSuggestions, setEmpSuggestions] = useState([]);
    const [empLoading, setEmpLoading] = useState(false);
    const empDebounceRef = useRef(null);

    // ═════════════════════════════════════════════════════════════════════════
    // DATA FETCHING
    // ═════════════════════════════════════════════════════════════════════════

    const fetchKpi = useCallback(async (preloaded = null) => {
        setKpiLoading(true);
        try {
            const res = preloaded ? { data: preloaded } : await getKpiSummary();
            if (res.data?.success) setKpi(res.data.data);
        } catch (e) {
            console.error('Failed to load KPIs:', e?.message || 'Unknown error');
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load KPIs' });
        } finally {
            setKpiLoading(false);
        }
    }, []);

    // Today's Activity — fired alongside KPIs so the "morning glance" tiles
    // appear at the same time as the rest of the top-of-page numbers.
    const fetchToday = useCallback(async (preloaded = null) => {
        setTodayLoading(true);
        try {
            const res = preloaded ? { data: preloaded } : await getTodaysActivity();
            if (res.data?.success) setToday(res.data.data);
        } catch (e) {
            // Non-fatal — tiles will show — but log so we can find regressions.
            console.error('Failed to load today’s activity:', e?.message || 'Unknown error');
        } finally {
            setTodayLoading(false);
        }
    }, []);

    const fetchLeaderboard = useCallback(async (preloaded = null) => {
        setLbLoading(true);
        try {
            const res = preloaded ? { data: preloaded } : await getLeaderboard({
                period: lbPeriod,
                sort_by: lbSort,
                sort_dir: lbSort === 'name' ? 'asc' : 'desc',
                search: lbSearch,
                page: lbPage,
                per_page: 10,
            });
            if (res.data?.success) {
                setLbData(res.data.data.data || []);
                setLbPagination(res.data.data.pagination || {});
            }
        } catch (e) {
            console.error('Failed to load leaderboard:', e?.message || 'Unknown error');
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load leaderboard' });
        } finally {
            setLbLoading(false);
        }
    }, [lbPeriod, lbSort, lbSearch, lbPage]);

    const fetchAgentLeaderboard = useCallback(async () => {
        setAgLoading(true);
        try {
            const res = await getAgentLeaderboard({
                period: agPeriod,
                sort_by: agSort,
                sort_dir: agSort === 'name' ? 'asc' : 'desc',
                search: agSearch,
                page: agPage,
                per_page: 10,
            });
            if (res.data?.success) {
                setAgData(res.data.data.data || []);
                setAgPagination(res.data.data.pagination || {});
            }
        } catch (e) {
            console.error('Failed to load agent leaderboard:', e?.message || 'Unknown error');
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load agent leaderboard' });
        } finally {
            setAgLoading(false);
        }
    }, [agPeriod, agSort, agSearch, agPage]);

    const fetchTrend = useCallback(async (preloaded = null) => {
        setTrendLoading(true);
        try {
            const res = preloaded ? { data: preloaded } : await getWeeklyTrend();
            if (res.data?.success) {
                const weeks = res.data.data;
                setTrend({
                    labels: weeks.map((w) => w.week_label),
                    datasets: [
                        {
                            label: 'Submitted',
                            data: weeks.map((w) => w.submitted),
                            borderColor: 'var(--blue-500)',
                            backgroundColor: 'rgba(59,130,246,0.1)',
                            tension: 0.3,
                            fill: true,
                        },
                        {
                            label: 'Approved',
                            data: weeks.map((w) => w.approved),
                            borderColor: 'var(--green-500)',
                            backgroundColor: 'rgba(34,197,94,0.1)',
                            tension: 0.3,
                            fill: true,
                        },
                    ],
                });
            }
        } catch (e) {
            console.error('Failed to load trend:', e?.message || 'Unknown error');
        } finally {
            setTrendLoading(false);
        }
    }, []);

    const fetchRejections = useCallback(async (preloaded = null) => {
        setRejLoading(true);
        try {
            const res = preloaded ? { data: preloaded } : await getRejectionReasons();
            if (res.data?.success) setRejections(res.data.data || []);
        } catch (e) {
            console.error('Failed to load rejections:', e?.message || 'Unknown error');
        } finally {
            setRejLoading(false);
        }
    }, []);

    const fetchActivity = useCallback(async (preloaded = null) => {
        setActLoading(true);
        try {
            const res = preloaded ? { data: preloaded } : await getRecentActivity();
            if (res.data?.success) setActivity(res.data.data || []);
        } catch (e) {
            console.error('Failed to load activity:', e?.message || 'Unknown error');
        } finally {
            setActLoading(false);
        }
    }, []);

    const fetchLeads = useCallback(async (page = 1, preloaded = null) => {
        setLeadsLoading(true);
        try {
            const params = { page, per_page: 15 };
            if (leadsSearch) params.search = leadsSearch;
            if (leadsStage) params.stage = leadsStage;
            if (leadsEmployee) params.employee_id = leadsEmployee;
            if (leadsDateFrom) params.date_from = leadsDateFrom.toISOString().split('T')[0];
            if (leadsDateTo) params.date_to = leadsDateTo.toISOString().split('T')[0];

            const res = preloaded ? { data: preloaded } : await getAllLeads(params);
            if (res.data?.success) {
                setLeads(res.data.data.data || []);
                setLeadsPag(res.data.data.pagination || {});
            }
        } catch (e) {
            console.error('Failed to load leads:', e?.message || 'Unknown error');
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load leads' });
        } finally {
            setLeadsLoading(false);
        }
    }, [leadsSearch, leadsStage, leadsEmployee, leadsDateFrom, leadsDateTo]);

    // Strip email from label — show name only
    const toNameOnly = (items) => items.map((i) => ({
        ...i,
        label: i.label?.replace(/\s*\(.*?\)\s*$/, '') || i.label,
    }));

    // Debounced employee search
    const makeEmpSearch = (setSuggestions, setLoading, debounceRef) => (query) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!query || query.length < 2) {
            setSuggestions([]);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const items = await adminLookupService.searchCompanyUsers(query);
                setSuggestions(toNameOnly(items));
            } catch (e) {
                setSuggestions([]);
            } finally {
                setLoading(false);
            }
        }, 300);
    };

    const searchLbEmployees = makeEmpSearch(setLbEmpSuggestions, setLbEmpLoading, lbEmpDebounceRef);
    const searchLeadsEmployees = makeEmpSearch(setEmpSuggestions, setEmpLoading, empDebounceRef);

    // ── Initial load: one request for all seven panels (#162) ────────────────
    //
    // This page used to fire seven GETs on mount — kpi, today, weekly-trend,
    // rejection-reasons, recent-activity, leaderboard, all-leads. They went out
    // together, so it was one round trip in wall-clock terms, but seven full
    // stacks: seven tenant resolutions, seven token lookups, and seven turns
    // through a dev server that serialises requests (#437).
    //
    // The aggregate seeds every panel from one response. The per-panel
    // endpoints stay exactly as they were and are still used for every
    // SUBSEQUENT fetch — changing a leaderboard period or a leads filter must
    // refetch that panel alone, not the whole page.
    // The in-flight aggregate, shared by the panel effects below rather than
    // suppressing them. Suppressing was the obvious approach and is subtly
    // wrong: if a filter changes while the aggregate is still in flight, the
    // suppressed effect never refetches and the panel is left showing results
    // for the OLD filter with the new one selected. Having each effect await
    // the aggregate instead means a filter change re-runs the effect normally,
    // cancels the previous pass, and fetches fresh.
    const bootstrap = useRef(null);
    const leaderboardFirstRun = useRef(true);
    const leadsFirstRun = useRef(true);

    useEffect(() => {
        let cancelled = false;

        bootstrap.current = fetchPageData('company-leads-dashboard', {
            // Nested bags rather than flattened prefixes, so each section
            // receives the parameter names its own endpoint already validates.
            leaderboard: {
                period: lbPeriod,
                sort_by: lbSort,
                sort_dir: lbSort === 'name' ? 'asc' : 'desc',
                search: lbSearch,
                page: lbPage,
                per_page: 10,
            },
            leads: {
                page: initialLeadsPage,
                per_page: 15,
                ...(leadsSearch ? { search: leadsSearch } : {}),
                ...(leadsStage ? { stage: leadsStage } : {}),
                ...(leadsEmployee ? { employee_id: leadsEmployee } : {}),
                ...(leadsDateFrom ? { date_from: leadsDateFrom.toISOString().split('T')[0] } : {}),
                ...(leadsDateTo ? { date_to: leadsDateTo.toISOString().split('T')[0] } : {}),
            },
            // The aggregate is an optimisation, not a dependency. If it is
            // unavailable — an older backend behind a stale deploy — every
            // consumer falls back to the endpoint that has always worked.
        }).catch((e) => {
            console.error('Page aggregate unavailable, falling back:', e?.message);
            return null;
        });

        (async () => {
            const page = await bootstrap.current;
            if (cancelled) return;

            fetchKpi(page?.section('kpi'));
            fetchToday(page?.section('today'));
            fetchTrend(page?.section('weekly_trend'));
            fetchRejections(page?.section('rejection_reasons'));
            fetchActivity(page?.section('recent_activity'));
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (leaderboardFirstRun.current) {
                leaderboardFirstRun.current = false;
                const page = await bootstrap.current;
                if (cancelled) return;
                if (page?.meta('leaderboard')?.status === 'ok') {
                    fetchLeaderboard(page.section('leaderboard'));
                    return;
                }
            }
            if (!cancelled) fetchLeaderboard();
        })();

        return () => { cancelled = true; };
    }, [fetchLeaderboard]);

    useEffect(() => {
        if (perfTab === 1) {
            fetchAgentLeaderboard();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [perfTab, agPeriod, agSort, agSearch, agPage]);

    // On first mount, respect the page restored from the URL — which the
    // aggregate was already given. On subsequent filter changes, reset to
    // page 1 and fetch that panel alone.
    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (leadsFirstRun.current) {
                leadsFirstRun.current = false;
                const page = await bootstrap.current;
                if (cancelled) return;
                if (page?.meta('all_leads')?.status === 'ok') {
                    fetchLeads(initialLeadsPage, page.section('all_leads'));
                    return;
                }
                if (!cancelled) fetchLeads(initialLeadsPage);
                return;
            }
            setLeadsFirst(0);
            if (!cancelled) fetchLeads(1);
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadsStage, leadsEmployee, leadsDateFrom, leadsDateTo]);

    // Sync All Leads filter + page state to URL query params so navigating back
    // restores exact position. Only non-default values are written.
    useEffect(() => {
        const next = new URLSearchParams();
        if (leadsSearch) next.set('search', leadsSearch);
        if (leadsStage) next.set('stage', leadsStage);
        if (leadsEmployee) next.set('employee', String(leadsEmployee));
        if (leadsDateFrom) next.set('from', leadsDateFrom.toISOString().split('T')[0]);
        if (leadsDateTo) next.set('to', leadsDateTo.toISOString().split('T')[0]);
        const currentPage = Math.floor(leadsFirst / 15) + 1;
        if (currentPage > 1) next.set('page', String(currentPage));
        // replace: true — don't pollute browser history with every keystroke
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadsSearch, leadsStage, leadsEmployee, leadsDateFrom, leadsDateTo, leadsFirst]);

    const handleLeadsSearch = () => {
        setLeadsFirst(0);
        fetchLeads(1);
    };

    const clearLeadsFilters = () => {
        setLeadsSearch('');
        setLeadsStage(null);
        setLeadsEmployee('');
        setEmpQuery('');
        setLeadsDateFrom(null);
        setLeadsDateTo(null);
        setLeadsFirst(0);
    };

    const refreshAll = () => {
        fetchKpi();
        fetchToday();
        fetchLeaderboard();
        if (perfTab === 1) fetchAgentLeaderboard();
        fetchTrend();
        fetchRejections();
        fetchActivity();
        fetchLeads(Math.floor(leadsFirst / 15) + 1);
    };

    const hasLeadsFilters = leadsSearch || leadsStage || leadsEmployee || leadsDateFrom || leadsDateTo;

    // Chart options
    const trendOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: { usePointStyle: true, pointStyle: 'circle', padding: 16, font: { size: 11 } },
            },
        },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 } } },
            y: { beginAtZero: true, grid: { color: 'var(--surface-200)' }, ticks: { font: { size: 10 }, stepSize: 1 } },
        },
    };

    return (
        <>
            <Toast ref={toast} position="top-right" />

            <div className="grid">
                <div className="col-12">
                    {/* Page header (P6) */}
                    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
                        <div>
                            <h1 className="text-xl font-semibold text-900 m-0 mb-1">Leads Dashboard</h1>
                            <p className="text-600 text-sm m-0">Company performance overview — KPIs, leaderboards, trends and leads</p>
                        </div>
                        <Button
                            icon="pi pi-refresh"
                            label="Refresh"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={refreshAll}
                            aria-label="Refresh all sections"
                        />
                    </div>

                    {/* ── ROW 0: Today's Activity (morning glance) ────────────
                        Six dense tiles answering "what's happened today + what's
                        in the pipeline." Lives above the monthly KPI cards on
                        purpose — managers open the page to see today first. */}
                    <div className="flex align-items-center gap-2 mb-2 mt-1">
                        <i className="pi pi-clock text-primary" aria-hidden="true" />
                        <span className="text-700 font-medium text-sm">Today's Activity</span>
                    </div>
                    <div className="grid mb-3">
                        <TodayTile
                            title="Leads Created"
                            icon="pi-user-plus"
                            color="blue"
                            value={today?.leads_created_today}
                            loading={todayLoading}
                            tooltip="New leads created today by anyone in the company"
                        />
                        <TodayTile
                            title="Submissions"
                            icon="pi-send"
                            color="cyan"
                            value={today?.submissions_today}
                            loading={todayLoading}
                            tooltip="Leads submitted to banks today"
                        />
                        <TodayTile
                            title="Approvals"
                            icon="pi-check-circle"
                            color="green"
                            value={today?.approvals_today}
                            loading={todayLoading}
                            tooltip="Bank approvals received today"
                        />
                        <TodayTile
                            title="Disbursals"
                            icon="pi-wallet"
                            color="teal"
                            value={today?.disbursals_today}
                            loading={todayLoading}
                            tooltip="Loans disbursed today"
                        />
                        <TodayTile
                            title="Stale Alerts"
                            icon="pi-exclamation-triangle"
                            color="orange"
                            value={today?.stale_alerts}
                            loading={todayLoading}
                            tooltip="Active leads idle for more than 7 days — likely leakage"
                        />
                        <TodayTile
                            title="Pipeline Value"
                            icon="pi-chart-line"
                            color="purple"
                            value={today?.pipeline_value}
                            loading={todayLoading}
                            isCurrency
                            tooltip="Total in-flight loan amount across all active leads"
                        />
                    </div>

                    {/* ── ROW 1: KPI Cards ──────────────────────────────────── */}
                    <div className="grid mb-3">
                        <KpiCard title="New Leads" icon="pi-briefcase" color="blue" data={kpi?.new_leads} loading={kpiLoading} />
                        <KpiCard title="Processing" icon="pi-spin pi-spinner" color="yellow" data={kpi?.processing} loading={kpiLoading} />
                        <KpiCard title="At Bank" icon="pi-building" color="cyan" data={kpi?.at_bank} loading={kpiLoading} />
                        <KpiCard title="Sanctioned" icon="pi-check-circle" color="green" data={kpi?.sanctioned} loading={kpiLoading} />
                        <KpiCard title="Disbursed" icon="pi-wallet" color="purple" data={kpi?.disbursed} loading={kpiLoading} />
                        <KpiCard title="Rejected" icon="pi-times-circle" color="red" data={kpi?.rejected} loading={kpiLoading} />
                    </div>

                    {/* ── ROW 2: Performance Leaderboard (Tabbed) ──────────────
                        TabView is outermost (no Card wrapping it) and each
                        TabPanel owns ONE Card — the DynamicLoanAdmin pattern.
                        This removes the old Card→TabView→panel triple padding
                        stack: now it's just TabView panel + one Card. */}
                    <TabView
                        className="mb-3"
                        activeIndex={perfTab}
                        onTabChange={(e) => setPerfTab(e.index)}
                        pt={{ panelcontainer: { className: 'p-0' } }}
                    >
                            <TabPanel header="Employee Performance">
                                <Card>
                                <div className="surface-ground p-2 md:p-3 border-round mb-3">
                                    <div className="grid">
                                        <div className="col-6 md:col">
                                            <Dropdown
                                                value={lbPeriod}
                                                options={PERIOD_OPTIONS}
                                                onChange={(e) => { setLbPeriod(e.value); setLbPage(1); }}
                                                className="w-full p-inputtext-sm"
                                                placeholder="Period"
                                                aria-label="Filter by period"
                                            />
                                        </div>
                                        <div className="col-6 md:col">
                                            <Dropdown
                                                value={lbSort}
                                                options={SORT_OPTIONS}
                                                onChange={(e) => { setLbSort(e.value); setLbPage(1); }}
                                                className="w-full p-inputtext-sm"
                                                placeholder="Sort by"
                                                aria-label="Sort order"
                                            />
                                        </div>
                                        <div className="col-12 md:col">
                                            <AutoComplete
                                                value={lbEmpQuery}
                                                suggestions={lbEmpSuggestions}
                                                completeMethod={(e) => searchLbEmployees(e.query)}
                                                field="label"
                                                onChange={(e) => {
                                                    if (typeof e.value === 'string') {
                                                        setLbEmpQuery(e.value);
                                                        if (!e.value) { setLbSearch(''); setLbPage(1); }
                                                    } else {
                                                        setLbEmpQuery(e.value.label);
                                                        setLbSearch(e.value.label);
                                                        setLbPage(1);
                                                    }
                                                }}
                                                placeholder="Search employee..."
                                                className="w-full"
                                                inputClassName="w-full p-inputtext-sm"
                                                loading={lbEmpLoading}
                                                minLength={2}
                                            />
                                        </div>
                                        <div className="hidden md:block md:col" aria-hidden="true"></div>
                                        <div className="col-12 md:col flex gap-2">
                                            <Button
                                                label="Clear"
                                                icon="pi pi-filter-slash"
                                                severity="secondary"
                                                outlined
                                                size="small"
                                                className="flex-1 py-1 md:py-2"
                                                onClick={() => {
                                                    setLbPeriod('this_month');
                                                    setLbSort('leads_created');
                                                    setLbSearch('');
                                                    setLbEmpQuery('');
                                                    setLbPage(1);
                                                }}
                                                disabled={lbPeriod === 'this_month' && lbSort === 'leads_created' && !lbSearch && !lbEmpQuery}
                                            />
                                            <Button
                                                icon="pi pi-refresh"
                                                label="Refresh"
                                                size="small"
                                                className="flex-1 py-1 md:py-2"
                                                onClick={() => fetchLeaderboard()}
                                                loading={lbLoading}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Desktop DataTable */}
                                <div className="hidden md:block">
                                    <DataTable
                                        value={lbData}
                                        loading={lbLoading}
                                        emptyMessage={
                                            <div className="flex flex-column align-items-center py-5">
                                                <i className="pi pi-users pi-2x text-400 mb-3" aria-hidden="true" />
                                                <span className="text-600">No employees found</span>
                                            </div>
                                        }
                                        size="small"
                                        stripedRows
                                        showGridlines
                                        className="p-datatable-gridlines"
                                        rowClassName={() => 'cursor-pointer'}
                                        onRowClick={(e) => {
                                            setLeadsEmployee(e.data.user_id);
                                            setLeadsFirst(0);
                                            fetchLeads(1);
                                        }}
                                        headerColumnGroup={
                                            // Two-row grouped header. The first row labels which group each
                                            // column belongs to so the manager knows whether a number describes
                                            // "what this employee created" (Activity) or "what they processed for
                                            // someone else" (Processing). The second row holds the actual column
                                            // names. colSpan on the parent headers must match the count of child
                                            // columns below.
                                            <ColumnGroup>
                                                <Row>
                                                    <Column header="" rowSpan={2} />
                                                    <Column
                                                        header={
                                                            <span className="text-blue-700 font-bold text-xs uppercase tracking-wider">
                                                                <i className="pi pi-user-plus mr-1" aria-hidden="true" />
                                                                Activity — Leads They Created
                                                            </span>
                                                        }
                                                        colSpan={5}
                                                        headerStyle={{ backgroundColor: 'var(--blue-50)', borderBottom: '2px solid var(--blue-200)' }}
                                                    />
                                                    <Column
                                                        header={
                                                            <span className="text-green-700 font-bold text-xs uppercase tracking-wider">
                                                                <i className="pi pi-cog mr-1" aria-hidden="true" />
                                                                Processing — Work They Handled
                                                            </span>
                                                        }
                                                        colSpan={7}
                                                        headerStyle={{ backgroundColor: 'var(--green-50)', borderBottom: '2px solid var(--green-200)' }}
                                                    />
                                                </Row>
                                                <Row>
                                                    {/* Activity sub-headers */}
                                                    <Column header="Created"      alignHeader="center" headerStyle={{ backgroundColor: 'var(--blue-50)' }} />
                                                    <Column header="Today"        alignHeader="center" headerStyle={{ backgroundColor: 'var(--blue-50)' }} />
                                                    <Column header="My Pipeline"  alignHeader="center" headerStyle={{ backgroundColor: 'var(--blue-50)' }} />
                                                    <Column header="₹ Value"      alignHeader="center" headerStyle={{ backgroundColor: 'var(--blue-50)' }} />
                                                    <Column header="Stale"        alignHeader="center" headerStyle={{ backgroundColor: 'var(--blue-50)' }} />
                                                    {/* Processing sub-headers */}
                                                    <Column header="Assigned"     alignHeader="center" headerStyle={{ backgroundColor: 'var(--green-50)' }} />
                                                    <Column header="Submitted"    alignHeader="center" headerStyle={{ backgroundColor: 'var(--green-50)' }} />
                                                    <Column header="Approved"     alignHeader="center" headerStyle={{ backgroundColor: 'var(--green-50)' }} />
                                                    <Column header="Rejected"     alignHeader="center" headerStyle={{ backgroundColor: 'var(--green-50)' }} />
                                                    <Column header="Conv %"       alignHeader="center" headerStyle={{ backgroundColor: 'var(--green-50)' }} />
                                                    <Column header="Avg Days"     alignHeader="center" headerStyle={{ backgroundColor: 'var(--green-50)' }} />
                                                    <Column header="In Progress"  alignHeader="center" headerStyle={{ backgroundColor: 'var(--green-50)' }} />
                                                </Row>
                                            </ColumnGroup>
                                        }
                                    >
                                        {/* Body columns. Headers come from the headerColumnGroup above — these
                                            Column elements only define `field` and `body` for each row's cells.
                                            Order MUST match the header columns (Employee, then 5 activity, then 7 processing). */}
                                        <Column
                                            field="name"
                                            style={{ minWidth: '12rem' }}
                                            body={(row) => (
                                                <div className="flex flex-column" style={{ lineHeight: 1.2 }}>
                                                    <span className="font-semibold text-sm text-900">{row.name}</span>
                                                    {row.email && <span className="text-xs text-500" title={row.email}>{row.email}</span>}
                                                </div>
                                            )}
                                        />

                                        {/* ── ACTIVITY (5 columns) ── */}
                                        <Column
                                            field="leads_created"
                                            alignHeader="center"
                                            bodyClassName="text-center"
                                            style={{ minWidth: '5rem' }}
                                            body={(row) => <span className="font-semibold text-blue-700">{row.leads_created}</span>}
                                        />
                                        <Column
                                            field="created_today"
                                            alignHeader="center"
                                            bodyClassName="text-center"
                                            style={{ minWidth: '4rem' }}
                                            body={(row) => row.created_today > 0
                                                ? <Tag value={row.created_today} severity="info" className="text-xs" />
                                                : <span className="text-400">0</span>
                                            }
                                        />
                                        <Column
                                            field="active_leads"
                                            alignHeader="center"
                                            bodyClassName="text-center"
                                            style={{ minWidth: '5rem' }}
                                            body={(row) => <span className="text-900">{row.active_leads}</span>}
                                        />
                                        <Column
                                            field="pipeline_value"
                                            alignHeader="center"
                                            bodyClassName="text-center"
                                            style={{ minWidth: '5.5rem' }}
                                            body={(row) => {
                                                const v = Number(row.pipeline_value || 0);
                                                if (v === 0) return <span className="text-400">—</span>;
                                                const formatted = v >= 10000000
                                                    ? `₹${(v / 10000000).toFixed(1)}Cr`
                                                    : v >= 100000
                                                        ? `₹${(v / 100000).toFixed(1)}L`
                                                        : `₹${v.toLocaleString('en-IN')}`;
                                                return <span className="text-purple-700 font-medium">{formatted}</span>;
                                            }}
                                        />
                                        <Column
                                            field="stale_leads"
                                            alignHeader="center"
                                            bodyClassName="text-center"
                                            style={{ minWidth: '4rem' }}
                                            body={(row) => row.stale_leads > 0
                                                ? <Tag value={row.stale_leads} severity="warning" className="text-xs" />
                                                : <span className="text-400">0</span>
                                            }
                                        />

                                        {/* ── PROCESSING (7 columns) ── */}
                                        <Column field="assigned"        alignHeader="center" bodyClassName="text-center" style={{ minWidth: '5rem' }} />
                                        <Column field="submitted"       alignHeader="center" bodyClassName="text-center" style={{ minWidth: '5.5rem' }} />
                                        <Column field="approved"        alignHeader="center" bodyClassName="text-center" style={{ minWidth: '5rem' }} body={(row) => <span className={row.approved > 0 ? 'text-green-600 font-semibold' : ''}>{row.approved}</span>} />
                                        <Column field="rejected"        alignHeader="center" bodyClassName="text-center" style={{ minWidth: '5rem' }} body={(row) => <span className={row.rejected > 0 ? 'text-red-600' : ''}>{row.rejected}</span>} />
                                        <Column field="conversion_rate" alignHeader="center" bodyClassName="text-center" style={{ minWidth: '4.5rem' }} body={(row) => <span className="font-semibold">{row.conversion_rate}%</span>} />
                                        <Column field="avg_days"        alignHeader="center" bodyClassName="text-center" style={{ minWidth: '5rem' }} body={(row) => row.avg_days != null ? `${Math.round(row.avg_days)}d` : '—'} />
                                        <Column field="active_now"      alignHeader="center" bodyClassName="text-center" style={{ minWidth: '5.5rem' }} body={(row) => row.active_now > 0 ? <Tag value={row.active_now} severity="info" className="text-xs" /> : '0'} />
                                    </DataTable>
                                </div>

                                {/* Mobile cards */}
                                <div className="block md:hidden">
                                    {lbLoading
                                        ? [1, 2, 3].map((i) => <Skeleton key={i} height="3.5rem" className="mb-2" />)
                                        : lbData.length === 0
                                            ? (
                                                <div className="flex flex-column align-items-center py-5">
                                                    <i className="pi pi-users pi-2x text-400 mb-3" aria-hidden="true" />
                                                    <span className="text-600">No employees found</span>
                                                </div>
                                            )
                                            : lbData.map((row) => (
                                                <div
                                                    key={row.user_id}
                                                    className="surface-hover border-round p-2 mb-2 cursor-pointer"
                                                    onClick={() => {
                                                        setLeadsEmployee(row.user_id);
                                                        setLeadsFirst(0);
                                                        fetchLeads(1);
                                                    }}
                                                >
                                                    <div className="flex align-items-center justify-content-between">
                                                        <span className="font-semibold text-sm text-900">{row.name}</span>
                                                        {row.stale_leads > 0 && (
                                                            <Tag value={`${row.stale_leads} stale`} severity="warning" className="text-xs" />
                                                        )}
                                                    </div>
                                                    {/* Activity row */}
                                                    <div className="flex flex-wrap gap-2 mt-1 text-xs">
                                                        <span className="text-blue-700">Created: <strong>{row.leads_created}</strong></span>
                                                        {row.created_today > 0 && <span className="text-blue-600">Today: <strong>{row.created_today}</strong></span>}
                                                        <span className="text-500">Active: <strong>{row.active_leads}</strong></span>
                                                    </div>
                                                    {/* Processing row */}
                                                    {(row.assigned > 0 || row.submitted > 0) && (
                                                        <div className="flex flex-wrap gap-2 mt-1 text-xs">
                                                            <span className="text-500">Assigned: <strong>{row.assigned}</strong></span>
                                                            <span className="text-500">Submitted: <strong>{row.submitted}</strong></span>
                                                            <span className="text-green-600">Approved: <strong>{row.approved}</strong></span>
                                                            <span className="text-500">{row.conversion_rate}% conv</span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                </div>

                                {lbPagination.last_page > 1 && (
                                    <Paginator
                                        first={(lbPage - 1) * 10}
                                        rows={10}
                                        totalRecords={lbPagination.total || 0}
                                        onPageChange={(e) => setLbPage(Math.floor(e.first / 10) + 1)}
                                        template="PrevPageLink PageLinks NextPageLink CurrentPageReport"
                                        currentPageReportTemplate="{first}-{last} of {totalRecords}"
                                        pageLinkSize={3}
                                        className="text-sm mt-2"
                                    />
                                )}
                                </Card>
                            </TabPanel>

                            <TabPanel header="Agent Performance">
                                <Card>
                                <div className="surface-ground p-2 md:p-3 border-round mb-3">
                                    <div className="grid">
                                        <div className="col-6 md:col">
                                            <Dropdown
                                                value={agPeriod}
                                                options={PERIOD_OPTIONS}
                                                onChange={(e) => { setAgPeriod(e.value); setAgPage(1); }}
                                                className="w-full p-inputtext-sm"
                                                placeholder="Period"
                                            />
                                        </div>
                                        <div className="col-6 md:col">
                                            <Dropdown
                                                value={agSort}
                                                options={AGENT_SORT_OPTIONS}
                                                onChange={(e) => { setAgSort(e.value); setAgPage(1); }}
                                                className="w-full p-inputtext-sm"
                                                placeholder="Sort by"
                                            />
                                        </div>
                                        <div className="col-12 md:col">
                                            <IconField iconPosition="left" className="w-full">
                                                <InputIcon className="pi pi-search" />
                                                <InputText
                                                    value={agSearch}
                                                    onChange={(e) => { setAgSearch(e.target.value); setAgPage(1); }}
                                                    placeholder="Search agent..."
                                                    className="w-full p-inputtext-sm"
                                                />
                                            </IconField>
                                        </div>
                                        <div className="hidden md:block md:col" aria-hidden="true"></div>
                                        <div className="col-12 md:col flex gap-2">
                                            <Button
                                                label="Clear"
                                                icon="pi pi-filter-slash"
                                                severity="secondary"
                                                outlined
                                                size="small"
                                                className="flex-1 py-1 md:py-2"
                                                onClick={() => {
                                                    setAgPeriod('this_month');
                                                    setAgSort('total_leads');
                                                    setAgSearch('');
                                                    setAgPage(1);
                                                }}
                                                disabled={agPeriod === 'this_month' && agSort === 'total_leads' && !agSearch}
                                            />
                                            <Button
                                                icon="pi pi-refresh"
                                                label="Refresh"
                                                size="small"
                                                className="flex-1 py-1 md:py-2"
                                                onClick={() => fetchAgentLeaderboard()}
                                                loading={agLoading}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Desktop DataTable */}
                                <div className="hidden md:block">
                                    <DataTable
                                        value={agData}
                                        loading={agLoading}
                                        emptyMessage={
                                            <div className="flex flex-column align-items-center py-5">
                                                <i className="pi pi-users pi-2x text-400 mb-3" aria-hidden="true" />
                                                <span className="text-600">No agents found</span>
                                            </div>
                                        }
                                        size="small"
                                        stripedRows
                                        showGridlines
                                        className="p-datatable-gridlines"
                                    >
                                        <Column field="name" header="Agent" style={{ minWidth: '10rem' }} body={(row) => <span className="font-semibold text-sm">{row.name}</span>} />
                                        <Column field="total_leads" header="Total Leads" alignHeader="center" bodyClassName="text-center" style={{ minWidth: '6rem' }} body={(row) => <span className="font-bold">{row.total_leads}</span>} />
                                        <Column field="in_progress" header="In Progress" alignHeader="center" bodyClassName="text-center" style={{ minWidth: '6rem' }} body={(row) => row.in_progress > 0 ? <Tag value={row.in_progress} severity="warning" className="text-xs" /> : '0'} />
                                        <Column field="approved" header="Approved" alignHeader="center" bodyClassName="text-center" style={{ minWidth: '6rem' }} body={(row) => <span className={row.approved > 0 ? 'text-green-600 font-semibold' : ''}>{row.approved}</span>} />
                                        <Column field="rejected" header="Rejected" alignHeader="center" bodyClassName="text-center" style={{ minWidth: '6rem' }} body={(row) => <span className={row.rejected > 0 ? 'text-red-600' : ''}>{row.rejected}</span>} />
                                        <Column field="disbursed" header="Disbursed" alignHeader="center" bodyClassName="text-center" style={{ minWidth: '6rem' }} body={(row) => <span className={row.disbursed > 0 ? 'text-green-700 font-bold' : ''}>{row.disbursed}</span>} />
                                        <Column field="quality_pct" header="Quality %" alignHeader="center" bodyClassName="text-center" style={{ minWidth: '5.5rem' }} body={(row) => row.quality_pct != null ? <span className={`font-semibold ${row.quality_pct >= 50 ? 'text-green-600' : 'text-red-600'}`}>{row.quality_pct}%</span> : '—'} />
                                        <Column field="conversion_pct" header="Conv %" alignHeader="center" bodyClassName="text-center" style={{ minWidth: '5rem' }} body={(row) => <span className="font-semibold">{row.conversion_pct}%</span>} />
                                        <Column
                                            header="Actions"
                                            alignHeader="center"
                                            bodyClassName="text-center"
                                            style={{ minWidth: '5rem', maxWidth: '5rem' }}
                                            body={(row) => (
                                                <Button
                                                    icon="pi pi-eye"
                                                    rounded
                                                    text
                                                    size="small"
                                                    severity="info"
                                                    aria-label={`View ${row.name}'s leads`}
                                                    tooltip="View leads"
                                                    tooltipOptions={{ position: 'top' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedAgent(row);
                                                    }}
                                                />
                                            )}
                                        />
                                    </DataTable>
                                </div>

                                {/* Mobile — Accordion (P17): collapsed = name +
                                    headline metric + View action; expand = every
                                    desktop column mirrored as label:value rows. */}
                                <div className="block md:hidden">
                                    {agLoading && (
                                        [1, 2, 3, 4].map((i) => (
                                            <Skeleton key={i} height="3.25rem" borderRadius="8px" className="mb-2" />
                                        ))
                                    )}

                                    {!agLoading && agData.length === 0 && (
                                        <div className="flex flex-column align-items-center py-5">
                                            <i className="pi pi-users pi-2x text-400 mb-3" aria-hidden="true" />
                                            <span className="text-600">No agents found</span>
                                        </div>
                                    )}

                                    {!agLoading && agData.length > 0 && (
                                        <Accordion
                                            activeIndex={agMobileIndex}
                                            onTabChange={(e) => setAgMobileIndex(e.index)}
                                        >
                                            {agData.map((row) => (
                                                <AccordionTab
                                                    key={row.agent_id}
                                                    header={
                                                        <div className="flex align-items-center justify-content-between gap-2 w-full">
                                                            <div className="flex flex-column min-w-0 flex-1">
                                                                <span className="font-semibold text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                                    {row.name}
                                                                </span>
                                                                <span className="text-500 text-xs">
                                                                    {row.total_leads} lead{row.total_leads === 1 ? '' : 's'}
                                                                    {row.quality_pct != null ? ` · ${row.quality_pct}% quality` : ''}
                                                                </span>
                                                            </div>
                                                            {/* stopPropagation so tapping View doesn't toggle the accordion (P18) */}
                                                            <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                                                                <Button
                                                                    icon="pi pi-eye"
                                                                    rounded
                                                                    text
                                                                    size="small"
                                                                    severity="info"
                                                                    aria-label={`View ${row.name}'s leads`}
                                                                    tooltip="View leads"
                                                                    tooltipOptions={{ position: 'left' }}
                                                                    onClick={() => setSelectedAgent(row)}
                                                                />
                                                            </div>
                                                        </div>
                                                    }
                                                >
                                                    {/* Expanded body — mirrors every desktop column */}
                                                    <div className="flex flex-column gap-2 text-sm">
                                                        {[
                                                            { label: 'Total Leads', value: row.total_leads, cls: 'text-900 font-semibold' },
                                                            { label: 'In Progress', value: row.in_progress, cls: row.in_progress > 0 ? 'text-orange-600 font-medium' : 'text-700' },
                                                            { label: 'Approved',    value: row.approved,    cls: row.approved > 0 ? 'text-green-600 font-medium' : 'text-700' },
                                                            { label: 'Rejected',    value: row.rejected,    cls: row.rejected > 0 ? 'text-red-600' : 'text-700' },
                                                            { label: 'Disbursed',   value: row.disbursed,   cls: row.disbursed > 0 ? 'text-green-700 font-semibold' : 'text-700' },
                                                            { label: 'Quality %',   value: row.quality_pct != null ? `${row.quality_pct}%` : '—', cls: 'text-700' },
                                                            { label: 'Conversion %', value: `${row.conversion_pct}%`, cls: 'text-700' },
                                                        ].map((m) => (
                                                            <div key={m.label} className="flex align-items-center justify-content-between">
                                                                <span className="text-500 font-medium">{m.label}</span>
                                                                <span className={m.cls}>{m.value}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </AccordionTab>
                                            ))}
                                        </Accordion>
                                    )}
                                </div>

                                {agPagination.last_page > 1 && (
                                    <Paginator
                                        first={(agPage - 1) * 10}
                                        rows={10}
                                        totalRecords={agPagination.total || 0}
                                        onPageChange={(e) => setAgPage(Math.floor(e.first / 10) + 1)}
                                        template="PrevPageLink PageLinks NextPageLink CurrentPageReport"
                                        currentPageReportTemplate="{first}-{last} of {totalRecords}"
                                        pageLinkSize={3}
                                        className="text-sm mt-2"
                                    />
                                )}
                                </Card>
                            </TabPanel>
                        </TabView>

                    {/* ── ROW 3: Trend + Rejections ────────────────────────── */}
                    <div className="grid mb-3">
                        {/* Weekly Trend */}
                        <div className="col-12 md:col-8">
                            <Card className="h-full">
                                <div className="flex align-items-center gap-2 mb-3">
                                    <i className="pi pi-chart-line text-primary" aria-hidden="true" />
                                    <h2 className="text-base font-medium text-900 m-0">Weekly Trend</h2>
                                </div>
                                {trendLoading ? (
                                    <Skeleton height="14rem" />
                                ) : trend ? (
                                    <div style={{ height: '16rem' }}>
                                        <Chart type="line" data={trend} options={trendOptions} />
                                    </div>
                                ) : (
                                    <div className="flex flex-column align-items-center py-5">
                                        <i className="pi pi-chart-line pi-2x text-400 mb-3" aria-hidden="true" />
                                        <span className="text-600 text-sm">No data available</span>
                                    </div>
                                )}
                            </Card>
                        </div>

                        {/* Rejection Reasons */}
                        <div className="col-12 md:col-4">
                            <Card className="h-full">
                                <div className="flex align-items-center gap-2 mb-3">
                                    <i className="pi pi-times-circle text-primary" aria-hidden="true" />
                                    <h2 className="text-base font-medium text-900 m-0">Top Rejection Reasons</h2>
                                </div>
                                {rejLoading ? (
                                    [1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height="1.75rem" className="mb-2" />)
                                ) : rejections.length === 0 ? (
                                    <div className="flex flex-column align-items-center py-3">
                                        <i className="pi pi-check-circle text-400 text-xl mb-2" aria-hidden="true" />
                                        <span className="text-600 text-sm">No rejections yet</span>
                                    </div>
                                ) : rejections.map((r, i) => (
                                    <div key={i} className="mb-3">
                                        <div className="flex align-items-center justify-content-between mb-1">
                                            <span className="text-700 text-sm">{r.label}</span>
                                            <span className="text-500 font-semibold text-xs">{r.count} ({r.percentage}%)</span>
                                        </div>
                                        <div className="border-round overflow-hidden" style={{ height: '0.4rem', backgroundColor: 'var(--surface-200)' }}>
                                            <div
                                                className="h-full border-round"
                                                style={{
                                                    width: `${r.percentage}%`,
                                                    backgroundColor: `var(--red-${Math.min(300 + i * 100, 700)})`,
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </Card>
                        </div>
                    </div>

                    {/* ── ROW 4: Recent Activity ────────────────────────────── */}
                    <Card className="mb-3">
                        <div className="flex align-items-center gap-2 mb-3">
                            <i className="pi pi-clock text-primary" aria-hidden="true" />
                            <h2 className="text-base font-medium text-900 m-0">Recent Activity</h2>
                        </div>
                        {actLoading ? (
                            [1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height="1.5rem" className="mb-2" />)
                        ) : activity.length === 0 ? (
                            <div className="flex flex-column align-items-center py-3">
                                <i className="pi pi-inbox text-400 text-xl mb-2" aria-hidden="true" />
                                <span className="text-600 text-sm">No recent activity</span>
                            </div>
                        ) : activity.map((item, i) => (
                            <div
                                key={i}
                                className="flex align-items-center gap-2 py-2"
                                style={{ borderBottom: i < activity.length - 1 ? '1px solid var(--surface-200)' : 'none' }}
                            >
                                <div
                                    className="flex-shrink-0 border-circle"
                                    style={{
                                        width: '0.6rem',
                                        height: '0.6rem',
                                        backgroundColor: ACTIVITY_COLORS[item.type] || 'var(--surface-400)',
                                    }}
                                />
                                <span className="text-700 text-sm flex-grow-1">{item.description}</span>
                                <span className="text-500 text-xs flex-shrink-0">{item.time_ago || timeAgo(item.timestamp)}</span>
                            </div>
                        ))}
                    </Card>

                    {/* ── ROW 5: All Leads List ─────────────────────────────── */}
                    <Card>
                        <div className="flex align-items-center gap-2 mb-3">
                            <i className="pi pi-list text-primary" aria-hidden="true" />
                            <h2 className="text-base font-medium text-900 m-0">All Leads</h2>
                        </div>

                        {/* Filter bar (P7 — 5 filters: search + employee + stage + from + to) */}
                        <div className="surface-ground p-3 border-round mb-3">
                            <div className="grid">
                                <div className="col-12 md:col">
                                    <IconField iconPosition="left" className="w-full">
                                        <InputIcon className="pi pi-search" />
                                        <InputText
                                            value={leadsSearch}
                                            onChange={(e) => setLeadsSearch(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleLeadsSearch()}
                                            placeholder="Lead ID..."
                                            className="w-full p-inputtext-sm"
                                        />
                                    </IconField>
                                </div>
                                <div className="col-12 md:col">
                                    <AutoComplete
                                        value={empQuery}
                                        suggestions={empSuggestions}
                                        completeMethod={(e) => searchLeadsEmployees(e.query)}
                                        field="label"
                                        onChange={(e) => {
                                            if (typeof e.value === 'string') {
                                                setEmpQuery(e.value);
                                                if (!e.value) { setLeadsEmployee(''); setLeadsFirst(0); fetchLeads(1); }
                                            } else {
                                                setEmpQuery(e.value.label);
                                                setLeadsEmployee(e.value.value);
                                                setLeadsFirst(0);
                                            }
                                        }}
                                        placeholder="Search employee..."
                                        className="w-full"
                                        inputClassName="w-full p-inputtext-sm"
                                        loading={empLoading}
                                        minLength={2}
                                    />
                                </div>
                                <div className="col-12 md:col">
                                    <Dropdown
                                        value={leadsStage}
                                        options={STAGE_OPTIONS}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => { setLeadsStage(e.value); setLeadsFirst(0); }}
                                        className="w-full p-inputtext-sm"
                                        placeholder="All Stages"
                                        showClear
                                    />
                                </div>
                                <div className="col-6 md:col">
                                    <Calendar
                                        value={leadsDateFrom}
                                        onChange={(e) => setLeadsDateFrom(e.value)}
                                        placeholder="From date"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        iconDisplay="input"
                                        className="w-full p-inputtext-sm"
                                        maxDate={leadsDateTo || new Date()}
                                    />
                                </div>
                                <div className="col-6 md:col">
                                    <Calendar
                                        value={leadsDateTo}
                                        onChange={(e) => setLeadsDateTo(e.value)}
                                        placeholder="To date"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        iconDisplay="input"
                                        className="w-full p-inputtext-sm"
                                        minDate={leadsDateFrom || undefined}
                                        maxDate={new Date()}
                                    />
                                </div>
                            </div>

                            {/* Row 2: action block (Clear + Apply) */}
                            <div className="grid mt-2">
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="col-12 md:col flex gap-2">
                                    <Button
                                        label="Clear"
                                        icon="pi pi-filter-slash"
                                        severity="secondary"
                                        outlined
                                        size="small"
                                        className="flex-1 py-1 md:py-2"
                                        onClick={clearLeadsFilters}
                                        disabled={!hasLeadsFilters}
                                    />
                                    <Button
                                        label="Apply"
                                        icon="pi pi-check"
                                        size="small"
                                        className="flex-1 py-1 md:py-2"
                                        onClick={handleLeadsSearch}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Desktop DataTable */}
                        <div className="hidden md:block">
                            <DataTable
                                value={leads}
                                loading={leadsLoading}
                                emptyMessage={
                                    <div className="flex flex-column align-items-center py-5">
                                        <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                                        <span className="text-600">No leads found</span>
                                    </div>
                                }
                                size="small"
                                stripedRows
                                showGridlines
                                className="p-datatable-gridlines"
                                rowClassName={() => 'cursor-pointer'}
                                onRowClick={(e) => navigate(`/app/leads/view/${e.data.lead_id}`)}
                            >
                                <Column
                                    field="lead_id_formatted"
                                    header="Lead"
                                    frozen
                                    alignFrozen="left"
                                    style={{ minWidth: '9rem' }}
                                    body={(row) => <span className="font-semibold text-sm text-primary">{row.lead_id_formatted}</span>}
                                />
                                <Column
                                    field="loan_amount"
                                    header="Amount"
                                    alignHeader="center"
                                    bodyClassName="text-center"
                                    style={{ minWidth: '6.5rem' }}
                                    body={(row) => <span className="text-blue-600 font-semibold text-sm">{formatCurrency(row.loan_amount)}</span>}
                                />
                                <Column
                                    field="processor"
                                    header="Processor"
                                    style={{ minWidth: '8rem' }}
                                    body={(row) => <span className="text-sm">{row.processor || '—'}</span>}
                                />
                                <Column
                                    field="lead_status"
                                    header="Status"
                                    alignHeader="center"
                                    bodyClassName="text-center"
                                    style={{ minWidth: '9rem' }}
                                    body={(row) => {
                                        const cfg = getProcessingStatusConfig(row.lead_status);
                                        return <Tag value={cfg.label} severity={cfg.severity} className="text-xs" />;
                                    }}
                                />
                                <Column
                                    field="bank_name"
                                    header="Bank"
                                    style={{ minWidth: '7rem' }}
                                    body={(row) => <span className="text-sm">{row.bank_name || '—'}</span>}
                                />
                                <Column
                                    field="broadcast_by"
                                    header="By"
                                    style={{ minWidth: '6rem' }}
                                    body={(row) => <span className="text-500 text-sm">{row.broadcast_by || '—'}</span>}
                                />
                                <Column
                                    field="days_active"
                                    header="Days"
                                    alignHeader="center"
                                    bodyClassName="text-center"
                                    style={{ minWidth: '5rem' }}
                                    body={(row) => <span className="text-500 text-sm">{row.days_active || '—'}</span>}
                                />
                            </DataTable>
                        </div>

                        {/* Mobile cards */}
                        <div className="block md:hidden">
                            {leadsLoading
                                ? [1, 2, 3].map((i) => <Skeleton key={i} height="4rem" className="mb-2" />)
                                : leads.length === 0
                                    ? (
                                        <div className="flex flex-column align-items-center py-5">
                                            <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                                            <span className="text-600">No leads found</span>
                                        </div>
                                    )
                                    : leads.map((lead) => {
                                        const cfg = getProcessingStatusConfig(lead.lead_status);
                                        return (
                                            <div
                                                key={lead.lead_id}
                                                className="border-bottom-1 surface-border py-2 cursor-pointer"
                                                onClick={() => navigate(`/app/leads/view/${lead.lead_id}`)}
                                            >
                                                <div className="flex align-items-center justify-content-between mb-1">
                                                    <div className="flex align-items-center gap-2">
                                                        <span className="font-semibold text-sm text-900">{lead.lead_id_formatted}</span>
                                                        <span className="text-blue-600 font-semibold text-xs">{formatCurrency(lead.loan_amount)}</span>
                                                    </div>
                                                    <Tag value={cfg.label} severity={cfg.severity} className="text-xs" />
                                                </div>
                                                <div className="flex flex-wrap gap-2 text-xs">
                                                    {lead.processor && (
                                                        <span className="text-600">
                                                            <i className="pi pi-user text-400 mr-1" aria-hidden="true" />{lead.processor}
                                                        </span>
                                                    )}
                                                    {lead.bank_name && <span className="text-500">{lead.bank_name}</span>}
                                                    <span className="text-500">{timeAgo(lead.updated_at)}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                        </div>

                        {leadsPag.last_page > 1 && (
                            <Paginator
                                first={leadsFirst}
                                rows={15}
                                totalRecords={leadsPag.total || 0}
                                onPageChange={(e) => { setLeadsFirst(e.first); fetchLeads(Math.floor(e.first / 15) + 1); }}
                                template="PrevPageLink PageLinks NextPageLink CurrentPageReport"
                                currentPageReportTemplate="{first}-{last} of {totalRecords}"
                                pageLinkSize={3}
                                className="text-sm mt-2"
                            />
                        )}
                    </Card>
                </div>
            </div>

            {/* Agent drill-down — opens when a row's "View leads" button is clicked
                in the Agent Performance tab. Embeds the same leads-listing UX as
                /app/leads so admins get a consistent view of each agent's leads. */}
            <AgentLeadsDrawer
                visible={!!selectedAgent}
                agent={selectedAgent}
                onHide={() => setSelectedAgent(null)}
            />
        </>
    );
};

export default CompanyLeadsDashboard;
